import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebSocketService } from './websocket.service';
import {
  StateReport,
  StateReportStatus,
  CreateStateReportRequest,
  UpdateStateReportRequest,
  SubmitStateReportRequest,
  StateReportProgressEvent,
  StateReportCompleteEvent,
  StateReportFailedEvent,
  STATE_REPORT_TYPE_VALUES,
  US_STATES,
  DEFAULT_STATE_REPORT_OPTIONS
} from '../models/state-report.model';

/**
 * API Response wrapper type
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * API List Response - matches backend controller response structure
 * Backend returns data as array directly with sibling pagination fields
 */
interface ApiListResponse<T> {
  success: boolean;
  data?: T[];
  total?: number;
  page?: number;
  limit?: number;
  error?: string;
}

/**
 * StateReportService
 *
 * Frontend service for state compliance report management.
 * Communicates with the backend API and handles real-time updates via WebSocket.
 *
 * Features:
 * - CRUD operations for state reports
 * - PDF generation triggering
 * - Real-time progress updates via WebSocket (no polling)
 * - Signal-based reactive state management
 *
 * Reference: /agent-os/specs/2026-01-16-state-reports/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class StateReportService implements OnDestroy {
  private http = inject(HttpClient);
  private webSocketService = inject(WebSocketService);
  private apiUrl = environment.apiUrl;
  private destroy$ = new Subject<void>();

  // Signal-based state
  private reportsSignal = signal<StateReport[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(10);

  // Public computed signals
  readonly reports = computed(() => this.reportsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Report type options
  readonly reportTypes = STATE_REPORT_TYPE_VALUES;
  readonly usStates = US_STATES;
  readonly defaultOptions = DEFAULT_STATE_REPORT_OPTIONS;

  // Computed stats
  readonly generatingReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'generating')
  );

  readonly readyReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'ready')
  );

  readonly failedReports = computed(() =>
    this.reportsSignal().filter(r => r.status === 'failed')
  );

  // Track WebSocket subscription state
  private isSubscribed = false;

  constructor() {
    // Subscribe to WebSocket events when connection state changes to authenticated
    this.webSocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /**
   * Set up WebSocket subscriptions for real-time state report updates.
   * Listens for progress, complete, and failed events.
   * Called when WebSocket connection is authenticated.
   */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    console.log('[StateReportService] Setting up WebSocket subscriptions for state report events');

    // Subscribe to progress events
    this.webSocketService.on<StateReportProgressEvent>('state-report:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[StateReportService] Received state-report:progress:', event);
        this.handleProgressEvent(event);
      });

    // Subscribe to complete events
    this.webSocketService.on<StateReportCompleteEvent>('state-report:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[StateReportService] Received state-report:complete:', event);
        this.handleCompleteEvent(event);
      });

    // Subscribe to failed events
    this.webSocketService.on<StateReportFailedEvent>('state-report:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[StateReportService] Received state-report:failed:', event);
        this.handleFailedEvent(event);
      });

    console.log('[StateReportService] WebSocket subscriptions established');
  }

  /**
   * Handle progress event from WebSocket
   */
  private handleProgressEvent(event: StateReportProgressEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            generationProgress: event.progress
          };
        }
        return report;
      });
    });
  }

  /**
   * Handle complete event from WebSocket
   */
  private handleCompleteEvent(event: StateReportCompleteEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            reportUrl: event.url,
            recordCount: event.recordCount,
            fileSize: event.fileSize,
            generationProgress: 100
          };
        }
        return report;
      });
    });
  }

  /**
   * Handle failed event from WebSocket
   */
  private handleFailedEvent(event: StateReportFailedEvent): void {
    this.reportsSignal.update(reports => {
      return reports.map(report => {
        if (report.id === event.reportId) {
          return {
            ...report,
            status: event.status,
            errorMessage: event.error,
            generationProgress: 0
          };
        }
        return report;
      });
    });
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * List state reports for an organization with pagination
   *
   * @param orgId Organization ID
   * @param options Optional query parameters (page, limit, status)
   * @returns Observable with paginated report list
   */
  listReports(
    orgId: string,
    options?: { page?: number; limit?: number; status?: StateReportStatus }
  ): Observable<ApiListResponse<StateReport>> {
    let params = new HttpParams();

    if (options?.page) {
      params = params.set('page', options.page.toString());
    }
    if (options?.limit) {
      params = params.set('limit', options.limit.toString());
    }
    if (options?.status) {
      params = params.set('status', options.status);
    }

    return this.http.get<ApiListResponse<StateReport>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports`,
      { params }
    );
  }

  /**
   * Load reports into signal state
   *
   * @param orgId Organization ID
   * @param options Optional query parameters
   */
  async loadReports(
    orgId: string,
    options?: { page?: number; limit?: number; status?: StateReportStatus }
  ): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(this.listReports(orgId, options));

      if (response.success && response.data) {
        // Backend returns data as array directly, with pagination as sibling fields
        this.reportsSignal.set(response.data.map(r => this.mapApiToReport(r)));
        this.totalCountSignal.set(response.total ?? 0);
        this.currentPageSignal.set(response.page ?? 1);
        this.pageSizeSignal.set(response.limit ?? 10);
      } else {
        throw new Error(response.error || 'Failed to load reports');
      }
    } catch (error: any) {
      console.error('Error loading state reports:', error);
      this.errorSignal.set(error.message || 'Failed to load reports');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get a single state report by ID
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with single report
   */
  getReport(orgId: string, reportId: string): Observable<ApiResponse<StateReport>> {
    return this.http.get<ApiResponse<StateReport>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}`
    );
  }

  /**
   * Fetch and return a single report
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with report or null
   */
  async fetchReport(orgId: string, reportId: string): Promise<StateReport | null> {
    try {
      const response = await firstValueFrom(this.getReport(orgId, reportId));

      if (response.success && response.data) {
        return this.mapApiToReport(response.data);
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching state report:', error);
      return null;
    }
  }

  /**
   * Create a new state report in draft status
   *
   * @param orgId Organization ID
   * @param data Report creation data
   * @returns Observable with created report
   */
  createReport(
    orgId: string,
    data: CreateStateReportRequest
  ): Observable<ApiResponse<StateReport>> {
    return this.http.post<ApiResponse<StateReport>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports`,
      data
    );
  }

  /**
   * Create report and add to signal state
   *
   * @param orgId Organization ID
   * @param data Report creation data
   * @returns Promise with created report or null
   */
  async createAndAddReport(
    orgId: string,
    data: CreateStateReportRequest
  ): Promise<StateReport | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(this.createReport(orgId, data));

      if (response.success && response.data) {
        const newReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => [newReport, ...reports]);
        this.totalCountSignal.update(count => count + 1);
        return newReport;
      } else {
        throw new Error(response.error || 'Failed to create report');
      }
    } catch (error: any) {
      console.error('Error creating state report:', error);
      this.errorSignal.set(error.message || 'Failed to create report');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing state report (only if status is draft)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param data Update data
   * @returns Observable with updated report
   */
  updateReport(
    orgId: string,
    reportId: string,
    data: UpdateStateReportRequest
  ): Observable<ApiResponse<StateReport>> {
    return this.http.put<ApiResponse<StateReport>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}`,
      data
    );
  }

  /**
   * Update report and refresh signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param data Update data
   * @returns Promise with updated report or null
   */
  async updateAndRefreshReport(
    orgId: string,
    reportId: string,
    data: UpdateStateReportRequest
  ): Promise<StateReport | null> {
    try {
      const response = await firstValueFrom(this.updateReport(orgId, reportId, data));

      if (response.success && response.data) {
        const updatedReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => {
          return reports.map(r => r.id === reportId ? updatedReport : r);
        });
        return updatedReport;
      } else {
        throw new Error(response.error || 'Failed to update report');
      }
    } catch (error: any) {
      console.error('Error updating state report:', error);
      this.errorSignal.set(error.message || 'Failed to update report');
      return null;
    }
  }

  /**
   * Delete a state report (soft delete)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with void response
   */
  deleteReport(orgId: string, reportId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}`
    );
  }

  /**
   * Delete report and remove from signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      // DELETE returns 204 No Content on success, so response body may be null
      const response = await firstValueFrom(this.deleteReport(orgId, reportId));

      // Treat null response (204) or response.success === true as success
      if (response === null || response?.success !== false) {
        this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
        this.totalCountSignal.update(count => Math.max(0, count - 1));
        return true;
      } else {
        throw new Error(response.error || 'Failed to delete report');
      }
    } catch (error: any) {
      console.error('Error deleting state report:', error);
      this.errorSignal.set(error.message || 'Failed to delete report');
      return false;
    }
  }

  /**
   * Generate PDF for a state report (queues background job)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with response
   */
  generatePdf(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
    return this.http.post<ApiResponse<{ jobId: string }>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}/generate`,
      {}
    );
  }

  /**
   * Trigger PDF generation and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.generatePdf(orgId, reportId));

      if (response.success) {
        // Update local state to show generating status
        this.reportsSignal.update(reports => {
          return reports.map(r => {
            if (r.id === reportId) {
              return {
                ...r,
                status: 'generating' as StateReportStatus,
                generationProgress: 0
              };
            }
            return r;
          });
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to start generation');
      }
    } catch (error: any) {
      console.error('Error generating state report PDF:', error);
      this.errorSignal.set(error.message || 'Failed to start generation');
      return false;
    }
  }

  /**
   * Retry a failed report generation
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Observable with response
   */
  retryReport(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
    return this.http.post<ApiResponse<{ jobId: string }>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}/retry`,
      {}
    );
  }

  /**
   * Retry failed report and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @returns Promise with success boolean
   */
  async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.retryReport(orgId, reportId));

      if (response.success) {
        // Update local state to show generating status
        this.reportsSignal.update(reports => {
          return reports.map(r => {
            if (r.id === reportId) {
              return {
                ...r,
                status: 'generating' as StateReportStatus,
                generationProgress: 0,
                errorMessage: undefined
              };
            }
            return r;
          });
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to retry generation');
      }
    } catch (error: any) {
      console.error('Error retrying state report:', error);
      this.errorSignal.set(error.message || 'Failed to retry generation');
      return false;
    }
  }

  /**
   * Submit a report (simulated - marks as submitted with email)
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param email Email address to submit to
   * @returns Observable with updated report
   */
  submitReport(
    orgId: string,
    reportId: string,
    email: string
  ): Observable<ApiResponse<StateReport>> {
    const body: SubmitStateReportRequest = { email };
    return this.http.post<ApiResponse<StateReport>>(
      `${this.apiUrl}/organizations/${orgId}/state-reports/${reportId}/submit`,
      body
    );
  }

  /**
   * Submit report and update signal state
   *
   * @param orgId Organization ID
   * @param reportId Report ID
   * @param email Email address to submit to
   * @returns Promise with success boolean
   */
  async submitAndUpdateReport(
    orgId: string,
    reportId: string,
    email: string
  ): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.submitReport(orgId, reportId, email));

      if (response.success && response.data) {
        const updatedReport = this.mapApiToReport(response.data);
        this.reportsSignal.update(reports => {
          return reports.map(r => r.id === reportId ? updatedReport : r);
        });
        return true;
      } else {
        throw new Error(response.error || 'Failed to submit report');
      }
    } catch (error: any) {
      console.error('Error submitting state report:', error);
      this.errorSignal.set(error.message || 'Failed to submit report');
      return false;
    }
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Map API response to StateReport interface
   * Handles both snake_case and camelCase field names
   */
  private mapApiToReport(data: any): StateReport {
    return {
      id: data.id,
      organizationId: data.organizationId ?? data.organization_id,
      name: data.name,
      state: data.state,
      reportType: data.reportType ?? data.report_type ?? 'all',
      status: data.status ?? 'draft',
      reportUrl: data.reportUrl ?? data.report_url,
      downloadUrl: data.downloadUrl ?? data.download_url,
      startDate: data.startDate ?? data.start_date,
      endDate: data.endDate ?? data.end_date,
      options: data.options ?? {
        includeChemicalDetails: true,
        includeApplicationRates: true,
        includeFieldMaps: true,
        includeWeatherConditions: true
      },
      generationProgress: data.generationProgress ?? data.generation_progress ?? 0,
      recordCount: data.recordCount ?? data.record_count,
      fileSize: data.fileSize ?? data.file_size,
      submittedAt: data.submittedAt ?? data.submitted_at,
      submittedTo: data.submittedTo ?? data.submitted_to,
      errorMessage: data.errorMessage ?? data.error_message,
      createdAt: data.createdAt ?? data.created_at,
      updatedAt: data.updatedAt ?? data.updated_at,
      createdBy: data.createdBy ?? data.created_by
    };
  }

  /**
   * Get report by ID from local state
   */
  getReportById(reportId: string): StateReport | undefined {
    return this.reportsSignal().find(r => r.id === reportId);
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: StateReportStatus): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating...';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /**
   * Get Bootstrap icon class for status
   */
  getStatusIcon(status: StateReportStatus): string {
    switch (status) {
      case 'draft': return 'bi-file-earmark';
      case 'generating': return 'bi-arrow-repeat';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-file-earmark';
    }
  }

  /**
   * Get report type label from value
   */
  getReportTypeLabel(value: string): string {
    return this.reportTypes.find(t => t.value === value)?.label || value;
  }

  /**
   * Get state name from state code
   */
  getStateLabel(stateCode: string): string {
    return this.usStates.find(s => s.value === stateCode)?.label || stateCode;
  }

  /**
   * Format date range for display
   */
  formatDateRange(startDate: string, endDate: string): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  }

  /**
   * Check if report can be deleted
   */
  canDelete(report: StateReport): boolean {
    return report.status !== 'generating';
  }

  /**
   * Check if report can be retried
   */
  canRetry(report: StateReport): boolean {
    return report.status === 'failed';
  }

  /**
   * Check if report can generate PDF
   */
  canGeneratePdf(report: StateReport): boolean {
    return report.status === 'draft';
  }

  /**
   * Check if report can be submitted
   */
  canSubmit(report: StateReport): boolean {
    return report.status === 'ready';
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
